import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Eye, Edit, Trash, Check, X, FilePlus, Calculator,
  ArrowLeftCircle, Undo2, LockKeyhole, UserPlus, LockKeyholeOpen, LogOut,
} from 'lucide-react';

const ActionMenu = forwardRef(({
  rowId,
  isOpen,
  onClose,
  moduleName,
  placement = "row",
  actions = [],
}, ref) => {

  const menuRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, ready: false });

  useEffect(() => {
    if (!isOpen) return;

    // Read the portal div's position (set by the caller via style.top/left)
    const portalParent = menuRef.current?.parentElement;
    if (!portalParent) return;

    const menuHeight = menuRef.current?.offsetHeight || 160;
    const parentTop = parseFloat(portalParent.style.top) || 0;
    const parentLeft = parseFloat(portalParent.style.left) || 0;

    const spaceBelow = window.innerHeight - (parentTop - window.scrollY);
    const flipUp = spaceBelow < menuHeight + 8;
    const offset = 25; // tweak this (px)

    setPosition({
      top: flipUp ? -(menuHeight + offset) : 0,
      ready: true,
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => onClose();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
}, [isOpen, onClose]);

  const IconComponent = ({ name }) => {
    const icons = {
      Eye, Edit, Trash, Check, X, FilePlus, Calculator,
      ArrowLeftCircle, Undo2, LockKeyhole, UserPlus, LockKeyholeOpen, LogOut,
    };
    const Icon = icons[name];
    return Icon ? <Icon size={16} className="shrink-0" /> : null;
  };

  const getHoverTextClass = (color) => {
    switch (color) {
      case 'blue':   return 'hover:text-blue-600 dark:hover:text-blue-400';
      case 'green':  return 'hover:text-green-600 dark:hover:text-green-400';
      case 'red':    return 'hover:text-red-600 dark:hover:text-red-400';
      case 'orange': return 'hover:text-orange-600 dark:hover:text-orange-400';
      case 'gray':   return 'hover:text-gray-600 dark:hover:text-gray-300';
      default:       return 'hover:text-gray-600 dark:hover:text-gray-300';
    }
  };

  return (
    <div
      ref={(node) => {
        menuRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      style={{
        position: 'absolute',
        top: position.top,
        left: 0,
        visibility: position.ready ? 'visible' : 'hidden', // prevent flash before flip is calculated
      }}
      className={`z-[9999] min-w-[120px]
        bg-white dark:bg-acs-dark-bg
        border border-gray-200 dark:border-acs-dark-border
        rounded-md shadow-lg transition-none
        ${isOpen ? 'animate-in fade-in-0 zoom-in-95' : 'animate-out fade-out-0 zoom-out-95'}`}
    >
      <div className="flex flex-col p-fluid-1.5">
        {actions.map((action, index) => (
          <button
            key={index}
            className={`flex items-center gap-fluid-1 px-fluid-2 py-fluid-1.5
              text-gray-700 dark:text-acs-dark-text
              text-fluid-xs sm:text-fluid-sm
              hover:bg-gray-50 dark:hover:bg-acs-dark-hover
              transition-none rounded-md font-roboto
              ${getHoverTextClass(action.color)}`}
            onClick={() => {
              action.action(rowId);
              onClose();
            }}
            aria-label={`${action.label} ${moduleName} ${rowId}`}
            disabled={action.disabled}
            title={action.tooltip}
          >
            <IconComponent name={action.icon} />
            <span className="truncate">{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
});

ActionMenu.propTypes = {
  rowId: PropTypes.any.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  moduleName: PropTypes.string.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      action: PropTypes.func.isRequired,
      disabled: PropTypes.bool,
      tooltip: PropTypes.string,
    })
  ),
};

export default ActionMenu;